import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { secureLog } from '@/lib/security';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, GripVertical, Trash, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { safeSaveText, sanitizeUrl } from '@/lib/utils';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Item {
  id: string;
  phase_id: string;
  item_text: string;
  notes: string | null;
  ads_url: string | null;
  is_secured: boolean;
  is_bundled: boolean;
  is_ready: boolean;
  display_order: number;
}

interface AdminItemManagerProps {
  phaseId: string;
  phaseTitle: string;
}

interface SortableRowProps {
  item: Item;
  updateItem: (id: string, field: keyof Item, value: string | boolean) => void;
  deleteItem: (id: string) => void;
}

const SortableRow = ({ item, updateItem, deleteItem }: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell className="w-12 text-center">
        <button
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      <TableCell className="w-12 text-center">
        <Checkbox
          checked={item.is_secured}
          onCheckedChange={(checked) => updateItem(item.id, 'is_secured', !!checked)}
        />
      </TableCell>
      <TableCell className="w-12 text-center">
        <Checkbox
          checked={item.is_bundled}
          onCheckedChange={(checked) => updateItem(item.id, 'is_bundled', !!checked)}
        />
      </TableCell>
      <TableCell className="w-12 text-center">
        <Checkbox
          checked={item.is_ready}
          onCheckedChange={(checked) => updateItem(item.id, 'is_ready', !!checked)}
        />
      </TableCell>
      <TableCell>
        <Input
          value={item.item_text}
          onChange={(e) => updateItem(item.id, 'item_text', e.target.value)}
          className="border-0 bg-transparent p-0 h-8 focus-visible:ring-1"
          placeholder="Enter item text..."
        />
      </TableCell>
      <TableCell>
        <Textarea
          value={item.notes || ''}
          onChange={(e) => updateItem(item.id, 'notes', e.target.value)}
          className="border-0 bg-transparent p-0 min-h-8 resize-none focus-visible:ring-1"
          placeholder="Enter notes..."
          rows={1}
        />
      </TableCell>
      <TableCell>
        <Input
          value={item.ads_url || ''}
          onChange={(e) => updateItem(item.id, 'ads_url', e.target.value)}
          className="border-0 bg-transparent p-0 h-8 focus-visible:ring-1"
          placeholder="https://..."
        />
      </TableCell>
      <TableCell className="w-16 text-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => deleteItem(item.id)}
          className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

export const AdminItemManager = ({ phaseId, phaseTitle }: AdminItemManagerProps) => {
  const [items, setItems] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadItems = async () => {
    try {
      const { data, error } = await supabase
        .from('template_items')
        .select('*')
        .eq('phase_id', phaseId)
        .order('display_order');
      
      if (error) throw error;
      setItems((data as Item[]) || []);
      setHasChanges(false);
      } catch (error) {
        secureLog.error('Error loading items in AdminItemManager', error);
      toast({
        title: "Error",
        description: "Failed to load items",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [phaseId]);

  const updateItem = (id: string, field: keyof Item, value: string | boolean) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
    setHasChanges(true);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const reorderedItems = arrayMove(items, oldIndex, newIndex);
        
        // Update display_order for all items
        const updatedItems = reorderedItems.map((item, index) => ({
          ...item,
          display_order: index
        }));
        
        setHasChanges(true);
        return updatedItems;
      });
    }
  };

  const addItem = () => {
    const maxOrder = Math.max(...items.map(i => i.display_order), -1);
    const newItem: Item = {
      id: crypto.randomUUID(),
      phase_id: phaseId,
      item_text: '',
      notes: null,
      ads_url: null,
      is_secured: false,
      is_bundled: false,
      is_ready: false,
      display_order: maxOrder + 1
    };
    setItems([...items, newItem]);
    setHasChanges(true);
  };

  const deleteItem = (itemId: string) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    setItems(items.filter(item => item.id !== itemId));
    setHasChanges(true);
  };

  const saveAllChanges = async () => {
    setIsSaving(true);
    try {
      // Filter out items with empty text
      const validItems = items.filter(item => item.item_text.trim());
      
      // Delete all existing items for this phase
      const { error: deleteError } = await supabase
        .from('template_items')
        .delete()
        .eq('phase_id', phaseId);
      
      if (deleteError) throw deleteError;

      // Insert all valid items
      if (validItems.length > 0) {
        const { error: insertError } = await supabase
          .from('template_items')
          .insert(validItems.map(item => ({
            id: item.id,
            phase_id: item.phase_id,
            item_text: safeSaveText(item.item_text, 500),
            notes: safeSaveText(item.notes || '', 1000) || null,
            ads_url: sanitizeUrl(item.ads_url?.trim() || '') || null,
            is_secured: item.is_secured,
            is_bundled: item.is_bundled,
            is_ready: item.is_ready,
            display_order: item.display_order
          })));
        
        if (insertError) throw insertError;
      }

      setHasChanges(false);
      toast({
        title: "Success",
        description: `Saved ${validItems.length} items successfully`
      });
      
      // Reload to get fresh data
      await loadItems();
      } catch (error) {
        secureLog.error('Error saving items in AdminItemManager', error);
      toast({
        title: "Error",
        description: "Failed to save items",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading items...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Template Items - {phaseTitle}</CardTitle>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <span className="text-sm text-muted-foreground">Unsaved changes</span>
            )}
            <Button 
              onClick={saveAllChanges} 
              disabled={!hasChanges || isSaving}
              size="sm"
              variant="accent"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save All'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12 text-center">Drag</TableHead>
                  <TableHead className="w-12 text-center">S</TableHead>
                  <TableHead className="w-12 text-center">B</TableHead>
                  <TableHead className="w-12 text-center">R</TableHead>
                  <TableHead className="min-w-[200px]">Item Text</TableHead>
                  <TableHead className="min-w-[150px]">Notes</TableHead>
                  <TableHead className="min-w-[150px]">Ads URL</TableHead>
                  <TableHead className="w-16 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <SortableContext
                  items={items.map(item => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {items.map((item) => (
                    <SortableRow
                      key={item.id}
                      item={item}
                      updateItem={updateItem}
                      deleteItem={deleteItem}
                    />
                  ))}
                </SortableContext>
              </TableBody>
            </Table>
          </div>
        </DndContext>
        
        <div className="mt-4">
          <Button 
            onClick={addItem} 
            variant="outline"
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Item ({items.length})
          </Button>
        </div>
        
        {/* Add explanation text for S, B, R */}
        {items.length > 0 && (
          <div className="bg-muted/20 p-3 rounded-lg text-sm mt-4">
            <div className="grid grid-cols-3 gap-4 text-muted-foreground text-xs">
              <div><strong>Secured (S):</strong> Item is available for purchase/use.</div>
              <div><strong>Bundled (B):</strong> Item should be grouped with others.</div>
              <div><strong>Ready (R):</strong> Item is ready for race day.</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};